const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '');

export interface BankFilePreview {
  messageId: string;
  filename: string;
  numberOfTransactions: number;
  duplicate: boolean;
  paymentInfos: {
    paymentInfoId: string;
    localInstrument: string | null;
    categoryPurpose: string | null;
    executionDate: string;
    transactions: number;
    totals: Record<string, number>;
  }[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface TwoFactorRequest {
  email: string;
  password: string;
  token: string;
}

export interface User {
  id: number;
  email: string;
  twoFactorEnabled: boolean;
  isAdmin: boolean;
}

export interface AuthResponse {
  user?: User;
  requiresTwoFactor?: boolean;
  message?: string;
  csrfToken?: string;
}

export interface BatchEvent {
  id: number;
  itemId: number | null;
  eventType: string;
  sourceFile: string | null;
  reason: string | null;
  supplierName: string | null;
  documentNumber: string | null;
  createdAt: string;
}

export interface JobStatus {
  name: string;
  isRunning: boolean;
  isExecuting: boolean;
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastSuccess: boolean | null;
  lastError: string | null;
  intervalMs: number | null;
}

async function extractErrorMessage(response: Response, fallback: string): Promise<string> {
  const text = await response.text();
  try {
    const parsed = JSON.parse(text);
    return parsed.message || fallback;
  } catch {
    return text || fallback;
  }
}

class ApiService {
  private csrfToken: string | null = null;

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }

    return headers;
  }

  setCsrfToken(token: string): void {
    this.csrfToken = token;
  }

  getCsrfToken(): string | null {
    return this.csrfToken;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(await extractErrorMessage(response, 'Login failed'));
    }

    const result = await response.json();
    if (result.csrfToken) {
      this.setCsrfToken(result.csrfToken);
    }
    return result;
  }

  async verifyTwoFactor(data: TwoFactorRequest): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/verify-2fa`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(await extractErrorMessage(response, 'Two-factor verification failed'));
    }

    const result = await response.json();
    if (result.csrfToken) {
      this.setCsrfToken(result.csrfToken);
    }
    return result;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email }),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await extractErrorMessage(response, 'Failed to send reset link'));
    }
    return response.json();
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ token, password }),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await extractErrorMessage(response, 'Failed to reset password'));
    }
    return response.json();
  }

  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to get current user');
    }

    const result = await response.json();
    if (result.csrfToken) {
      this.setCsrfToken(result.csrfToken);
    }
    return result.user;
  }

  async getTwoFactorSetup(): Promise<{ secret: string; qrCode: string; csrfToken?: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/2fa/setup`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to get two-factor setup');
    }

    const result = await response.json();
    if (result.csrfToken) {
      this.setCsrfToken(result.csrfToken);
    }
    return result;
  }

  async enableTwoFactor(token: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/2fa/enable`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ token }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to enable two-factor authentication');
    }

    return response.json();
  }

  async disableTwoFactor(): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/2fa/disable`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to disable two-factor authentication');
    }

    return response.json();
  }

  async logout(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to logout');
    }

    this.csrfToken = null;
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      this.csrfToken = null;
      return false;
    }
  }

  async getInvoices(): Promise<{ invoices: any[] }> {
    const response = await fetch(`${API_BASE_URL}/payments/invoices`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to fetch invoices' }));
      throw new Error(err.message);
    }
    return response.json();
  }

  async exportPayments(
    documentNumbers: string[],
    executionDate: string,
    groupByDueDate?: boolean,
  ): Promise<{ batches: Array<{ id: number; batchReference: string; executionDate: string; itemCount: number }> }> {
    const response = await fetch(`${API_BASE_URL}/payments/export`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ documentNumbers, executionDate, groupByDueDate }),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(err.message);
    }
    return response.json();
  }

  async exportThirdParty(documentNumbers: string[], executionDate: string, groupByDueDate?: boolean): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payments/export-third-party`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ documentNumbers, executionDate, groupByDueDate }),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(err.message);
    }
  }

  async downloadBatch(batchId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/${batchId}/download`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Download failed' }));
      throw new Error(err.message);
    }
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : `batch-${batchId}`;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getBatches(params?: { limit?: number; offset?: number; status?: string; batchType?: string; currency?: string }): Promise<{ batches: any[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    if (params?.status) qs.set('status', params.status);
    if (params?.batchType) qs.set('batchType', params.batchType);
    if (params?.currency) qs.set('currency', params.currency);
    const response = await fetch(`${API_BASE_URL}/payments/batches?${qs}`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch batches');
    return response.json();
  }

  async getBatchCurrencies(): Promise<{ currencies: string[] }> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/currencies`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch batch currencies');
    return response.json();
  }

  async getBatchEvents(batchId: number): Promise<{ events: BatchEvent[] }> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/${batchId}/events`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch batch events');
    return response.json();
  }

  async confirmBatch(batchId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/${batchId}/confirm`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to send batch to bank' }));
      throw new Error(err.message);
    }
  }

  async pollBank(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payments/poll`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Poll failed' }));
      throw new Error(err.message);
    }
  }

  async cancelBatch(batchId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/${batchId}/cancel`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to cancel batch' }));
      throw new Error(err.message);
    }
  }

  async markBatchPaid(batchId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/${batchId}/mark-paid`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to mark batch as paid' }));
      throw new Error(err.message);
    }
  }

  async forceFortnoxPay(batchId: number): Promise<{ message: string; queued: number }> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/${batchId}/force-fortnox-pay`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    const result = await response.json().catch(() => ({ message: 'Failed to force Fortnox payment' }));
    if (!response.ok) {
      throw new Error(result.message);
    }
    return result;
  }

  async deleteBatch(batchId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/${batchId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to delete batch' }));
      throw new Error(err.message);
    }
  }

  async resubmitFailed(batchId: number): Promise<{ newBatchId: number; batchReference: string }> {
    const response = await fetch(`${API_BASE_URL}/payments/batches/${batchId}/resubmit-failed`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Resubmit failed' }));
      throw new Error(err.message);
    }
    return response.json();
  }

  async getJobs(): Promise<{ jobs: JobStatus[] }> {
    const response = await fetch(`${API_BASE_URL}/jobs`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch jobs');
    return response.json();
  }

  async previewBankFile(xmlContent: string): Promise<BankFilePreview> {
    const response = await fetch(`${API_BASE_URL}/bank-files/preview`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ xmlContent }),
      credentials: 'include',
    });
    const result = await response.json().catch(() => ({ message: 'Failed to read file' }));
    if (!response.ok) {
      throw new Error(result.message);
    }
    return result;
  }

  async uploadBankFile(xmlContent: string): Promise<{ message: string; filename: string }> {
    const response = await fetch(`${API_BASE_URL}/bank-files/upload`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ xmlContent }),
      credentials: 'include',
    });
    const result = await response.json().catch(() => ({ message: 'Failed to send file' }));
    if (!response.ok) {
      throw new Error(result.message);
    }
    return result;
  }

  async runJob(name: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/jobs/${name}/run`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to run job' }));
      throw new Error(err.message);
    }
  }

  async getTransactions(): Promise<{ transactions: any[] }> {
    const response = await fetch(`${API_BASE_URL}/transactions`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch transactions');
    return response.json();
  }

  async createTransaction(data: {
    transaction_date: string;
    amount: number;
    currency: string;
    reference?: string;
    description?: string;
  }): Promise<{ transaction: any }> {
    const response = await fetch(`${API_BASE_URL}/transactions`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to create transaction' }));
      throw new Error(err.message);
    }
    return response.json();
  }

  async matchTransaction(id: number, invoiceDocumentNumber: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/transactions/${id}/match`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ invoiceDocumentNumber }),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to match transaction' }));
      throw new Error(err.message);
    }
  }

  async unmatchTransaction(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/transactions/${id}/unmatch`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to unmatch transaction');
  }

  async getCustomerInvoiceLines(documentNumber: string): Promise<{ lines: any[] }> {
    const response = await fetch(`${API_BASE_URL}/invoices/customer/${encodeURIComponent(documentNumber)}`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to fetch invoice lines' }));
      throw new Error(err.message);
    }
    return response.json();
  }

  async getCustomerInvoices(): Promise<{ invoices: any[] }> {
    const response = await fetch(`${API_BASE_URL}/invoices/customer`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to fetch customer invoices' }));
      throw new Error(err.message);
    }
    return response.json();
  }

  async getPaymentAccounts(): Promise<{ accounts: PaymentAccount[] }> {
    const response = await fetch(`${API_BASE_URL}/settings/payment-accounts`, {
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch payment accounts');
    return response.json();
  }

  async savePaymentAccount(
    currencyGroup: 'SEK' | 'EUR' | 'OTHER',
    data: { account_number: string; company_name: string; bic: string }
  ): Promise<{ account: PaymentAccount }> {
    const response = await fetch(`${API_BASE_URL}/settings/payment-accounts/${currencyGroup}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to save payment account' }));
      throw new Error(err.message);
    }
    return response.json();
  }

  async parsePayrollFile(fileContent: string): Promise<PayrollParseResult> {
    const response = await fetch(`${API_BASE_URL}/payroll/parse`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ fileContent }),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Parse failed' }));
      throw new Error(err.message);
    }
    return response.json();
  }

  async confirmPayrollBatch(payload: PayrollConfirmPayload): Promise<{ batchId: number; batchReference: string; xml: string; filename: string; skipped: string[] }> {
    const response = await fetch(`${API_BASE_URL}/payroll/confirm`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Confirm failed' }));
      throw new Error(err.message);
    }
    return response.json();
  }
}

export interface PayrollRow {
  account: string;
  amount: number;
  personnummer: string;
  description: string;
  valueDate: string;
  employeeName: string | null;
  citiAccountFormat: string | null;
}

export interface PayrollParseResult {
  paymentDate: string;
  currency: string;
  rows: PayrollRow[];
  unknownEmployees: string[];
  missingBankInfo: string[];
  invalidBankInfo: string[];
}

export interface PayrollConfirmPayload {
  rows: PayrollRow[];
  paymentDate: string;
  currency: string;
}

export interface PaymentAccount {
  id: number;
  currency_group: 'SEK' | 'EUR' | 'OTHER';
  account_number: string;
  company_name: string;
  bic: string;
  updated_at: string;
}

export const apiService = new ApiService();